import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { applyColors } from '../contexts/ThemeContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';
import MLB_THEMES from '../mlbThemes';
import {
  BuildingOfficeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import OrganizationEmailModal from '../components/OrganizationEmailModal';
import OrganizationSMSModal from '../components/OrganizationSMSModal';
import OrganizationEmailTemplatesModal from '../components/OrganizationEmailTemplatesModal';
import UserManagement from './UserManagement';
import AgeGroupManagement from '../components/AgeGroupManagement';

const TABS = [
  { key: 'organizations', label: 'Organizations' },
  { key: 'users', label: 'Users' },
  { key: 'age-groups', label: 'Age Groups' }
];

const OrganizationManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('organizations');
  const [usersTabOrgId, setUsersTabOrgId] = useState('');
  const [confirmState, setConfirmState] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedOrgForEmail, setSelectedOrgForEmail] = useState(null);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [selectedOrgForSMS, setSelectedOrgForSMS] = useState(null);
  const [showEmailTemplatesModal, setShowEmailTemplatesModal] = useState(false);
  const [selectedOrgForTemplates, setSelectedOrgForTemplates] = useState(null);

  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  // Theme selection state
  const [selectedTheme, setSelectedTheme] = useState(null);

  // Sponsor management state
  const [sponsors, setSponsors] = useState([]);
  const [sponsorForm, setSponsorForm] = useState({ name: '', logo: '', website: '' });
  const [selectedParentOrgId, setSelectedParentOrgId] = useState('');

  // Sport state
  const [selectedSport, setSelectedSport] = useState('baseball');
  const [publicSlug, setPublicSlug] = useState('');
  const [publicSite, setPublicSite] = useState({
    enabled: false,
    headline: '',
    welcomeText: '',
    heroImageUrl: ''
  });

  // Scheduling state
  const [schedulingEnabled, setSchedulingEnabled] = useState(false);

  // Itemization state
  const [itemizationEnabled, setItemizationEnabled] = useState(false);
  const [lineItemTemplates, setLineItemTemplates] = useState([]);
  const [lineItemForm, setLineItemForm] = useState({ name: '', description: '', default_amount: '', category: '' });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, [user]);

  // Default the Users tab's org switcher to the first org once loaded.
  useEffect(() => {
    if (!usersTabOrgId && organizations.length > 0) {
      setUsersTabOrgId(String(organizations[0].id));
    }
  }, [organizations, usersTabOrgId]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/organizations/my-organizations', {
        params: { _t: Date.now() },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const orgUsers = Array.isArray(response.data) ? response.data : [];

      const transformed = orgUsers.map(orgUser => {
        const org = orgUser.Organization || orgUser;
        if (org && (org.id || orgUser.organization_id)) {
          return {
            id: org.id || orgUser.organization_id,
            name: org.name || 'Unknown Organization',
            description: org.description || '',
            organizationCode: org.organizationCode || '',
            logo: org.logo || null,
            colors: org.colors || null,
            contactInfo: org.contactInfo || {},
            sponsors: org.sponsors || [],
            sport: org.sport || 'baseball',
            settings: org.settings || {},
            publicSlug: org.public_slug || '',
            publicSite: org.public_site || {},
            parent_organization_id: org.parent_organization_id || null,
            parent: org.ParentOrganization || null,
            children: org.ChildOrganizations || [],
            role: orgUser.role,
            isPrimary: orgUser.is_primary,
            createdAt: org.created_at || org.createdAt
          };
        }
        return {
          id: orgUser.organization_id || null,
          name: orgUser.name || 'Unknown Organization',
          description: orgUser.description || '',
          organizationCode: orgUser.organizationCode || '',
          logo: orgUser.logo || null,
          colors: orgUser.colors || null,
          contactInfo: orgUser.contactInfo || {},
          sponsors: orgUser.sponsors || [],
          sport: orgUser.sport || 'baseball',
          settings: orgUser.settings || {},
          publicSlug: orgUser.public_slug || '',
          publicSite: orgUser.public_site || {},
          parent_organization_id: null,
          parent: null,
          children: [],
          role: orgUser.role,
          isPrimary: orgUser.is_primary,
          createdAt: orgUser.created_at || orgUser.createdAt
        };
      }).filter(org => !!org.id);

      const orgsWithStats = await Promise.all(transformed.map(async (org) => {
        try {
          const orgDetailsResponse = await axios.get(`/api/organizations/${org.id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          const orgDetails = orgDetailsResponse.data;
          const detailOrg = orgDetails.organization || {};
          return {
            ...org,
            settings: detailOrg.settings || {},
            publicSlug: detailOrg.public_slug ?? org.publicSlug ?? '',
            publicSite: detailOrg.public_site || org.publicSite || {},
            contactInfo: detailOrg.contactInfo || org.contactInfo || {},
            teams: orgDetails.stats?.teamCount || 0,
            members: orgDetails.stats?.totalPlayers || 0,
            coaches: 0,
            status: 'active',
            parent_organization_id: detailOrg.parent_organization_id ?? org.parent_organization_id ?? null,
            parent: detailOrg.ParentOrganization || org.parent || null,
            children: detailOrg.ChildOrganizations || org.children || []
          };
        } catch {
          return { ...org, teams: 0, members: 0, coaches: 0, status: 'active' };
        }
      }));

      setOrganizations(orgsWithStats);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrganization = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = (formData.get('name') || '').toString().trim();
    if (!name) { toast.error('Organization name is required'); return; }
    try {
      await axios.post('/api/organizations', {
        name,
        description: '',
        parent_organization_id: selectedParentOrgId ? parseInt(selectedParentOrgId, 10) : null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Organization created!');
      setShowAddForm(false);
      setSelectedParentOrgId('');
      e.target.reset();
      await fetchOrganizations();
    } catch (error) {
      const msg = error.response?.data?.errors?.[0]?.msg || error.response?.data?.message || 'Failed to create organization';
      toast.error(msg);
    }
  };

  const handleEditOrganization = async (org) => {
    setEditingOrg(org);
    setLogoFile(null);
    setLogoPreview(org.logo || null);
    // Pre-select matching MLB theme if colors exist (match on both primary + secondary)
    if (org.colors?.primary) {
      const match = MLB_THEMES.find(t =>
        t.primary.toLowerCase() === org.colors.primary.toLowerCase() &&
        t.secondary.toLowerCase() === (org.colors.secondary || '').toLowerCase()
      ) || MLB_THEMES.find(t => t.primary.toLowerCase() === org.colors.primary.toLowerCase());
      setSelectedTheme(match || null);
    } else {
      setSelectedTheme(null);
    }
    // Pre-populate sponsors
    setSponsors(org.sponsors || []);
    setSponsorForm({ name: '', logo: '', website: '' });
    setSelectedParentOrgId(org.parent_organization_id ? String(org.parent_organization_id) : '');
    setSelectedSport(org.sport || 'baseball');
    setPublicSlug(org.publicSlug || '');
    setPublicSite({
      enabled: org.publicSite?.enabled === true,
      headline: org.publicSite?.headline || '',
      welcomeText: org.publicSite?.welcomeText || '',
      heroImageUrl: org.publicSite?.heroImageUrl || ''
    });

    // Initialize scheduling state
    setSchedulingEnabled(org.settings?.schedulingEnabled || false);

    // Initialize itemization state
    const enabled = org.settings?.itemizationEnabled || false;
    setItemizationEnabled(enabled);
    setLineItemForm({ name: '', description: '', default_amount: '', category: '' });
    setEditingTemplate(null);

    if (enabled) {
      fetchLineItemTemplates(org.id);
    } else {
      setLineItemTemplates([]);
    }

    setShowAddForm(true);
  };

  const fetchLineItemTemplates = async (orgId) => {
    try {
      setLineItemsLoading(true);
      const res = await axios.get(`/api/organizations/${orgId}/line-item-templates`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setLineItemTemplates(Array.isArray(res.data) ? res.data : []);
    } catch {
      setLineItemTemplates([]);
    } finally {
      setLineItemsLoading(false);
    }
  };

  const handleItemizationToggle = async (newValue) => {
    setItemizationEnabled(newValue);
    if (newValue && editingOrg) {
      fetchLineItemTemplates(editingOrg.id);
    } else {
      setLineItemTemplates([]);
    }
  };

  const handleSaveLineItemTemplate = async () => {
    if (!lineItemForm.name.trim()) return;
    try {
      if (editingTemplate) {
        const res = await axios.put(
          `/api/organizations/${editingOrg.id}/line-item-templates/${editingTemplate.id}`,
          lineItemForm,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        setLineItemTemplates(prev => prev.map(t => t.id === editingTemplate.id ? res.data : t));
      } else {
        const res = await axios.post(
          `/api/organizations/${editingOrg.id}/line-item-templates`,
          lineItemForm,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        setLineItemTemplates(prev => [...prev, res.data]);
      }
      setLineItemForm({ name: '', description: '', default_amount: '', category: '' });
      setEditingTemplate(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save item');
    }
  };

  const handleDeleteLineItemTemplate = async (templateId) => {
    try {
      await axios.delete(
        `/api/organizations/${editingOrg.id}/line-item-templates/${templateId}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      setLineItemTemplates(prev => prev.filter(t => t.id !== templateId));
      if (editingTemplate?.id === templateId) {
        setEditingTemplate(null);
        setLineItemForm({ name: '', description: '', default_amount: '', category: '' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const startEditTemplate = (template) => {
    setEditingTemplate(template);
    setLineItemForm({
      name: template.name,
      description: template.description || '',
      default_amount: template.default_amount != null ? String(template.default_amount) : '',
      category: template.category || ''
    });
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const addSponsor = () => {
    if (!sponsorForm.name.trim()) return;
    setSponsors(prev => [...prev, {
      id: Date.now().toString(),
      name: sponsorForm.name.trim(),
      logo: sponsorForm.logo.trim() || null,
      website: sponsorForm.website.trim() || null
    }]);
    setSponsorForm({ name: '', logo: '', website: '' });
  };

  const removeSponsor = (id) => setSponsors(prev => prev.filter(s => s.id !== id));

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      // Upload logo file first if one was selected
      let logoPath = editingOrg?.logo || null;
      if (logoFile) {
        setLogoUploading(true);
        const uploadData = new FormData();
        uploadData.append('logo', logoFile);
        const uploadRes = await axios.post(`/api/organizations/${editingOrg.id}/logo`, uploadData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        logoPath = uploadRes.data.logo;
        setLogoUploading(false);
      }

      const payload = {
        name: (formData.get('name') || '').toString().trim(),
        logo: logoPath,
        description: editingOrg?.description || '',
        colors: selectedTheme
          ? { primary: selectedTheme.primary, secondary: selectedTheme.secondary }
          : editingOrg?.colors || { primary: '#2563eb', secondary: '#1d4ed8' },
        sponsors,
        sport: selectedSport,
        contactInfo: {
          ...(editingOrg?.contactInfo || {}),
          email: formData.has('publicContactEmail')
            ? (formData.get('publicContactEmail') || '').toString().trim() || null
            : editingOrg?.contactInfo?.email || null,
          phone: formData.has('publicContactPhone')
            ? (formData.get('publicContactPhone') || '').toString().trim() || null
            : editingOrg?.contactInfo?.phone || null
        },
        settings: {
          ...(editingOrg?.settings || {}),
          itemizationEnabled,
          schedulingEnabled
        },
        parent_organization_id: selectedParentOrgId ? parseInt(selectedParentOrgId, 10) : null
      };

      await axios.put(`/api/organizations/${editingOrg.id}`, payload, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const canManagePublicSite = ['owner', 'admin'].includes(editingOrg.role)
        || ['admin', 'app_manager'].includes(user?.role);
      if (canManagePublicSite && publicSlug.trim()) {
        await axios.put(`/api/organizations/${editingOrg.id}/public-site`, {
          publicSlug,
          publicSite
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
      } else if (canManagePublicSite && publicSite.enabled) {
        throw new Error('Choose a subdomain before publishing the public website.');
      }

      // Apply theme immediately if this is the user's primary org and persist to cache
      if (editingOrg.isPrimary && selectedTheme) {
        const newColors = { primary: selectedTheme.primary, secondary: selectedTheme.secondary };
        applyColors(newColors);
        try { localStorage.setItem('theme_colors', JSON.stringify(newColors)); } catch (_) {}
      }

      toast.success('Organization updated!');
      await fetchOrganizations();
      setEditingOrg(null);
      setShowAddForm(false);
      setLogoFile(null);
      setLogoPreview(null);
      setSelectedTheme(null);
      setSponsors([]);
      setSponsorForm({ name: '', logo: '', website: '' });
      setItemizationEnabled(false);
      setSchedulingEnabled(false);
      setLineItemTemplates([]);
      setLineItemForm({ name: '', description: '', default_amount: '', category: '' });
      setEditingTemplate(null);
      setSelectedSport('baseball');
      setPublicSlug('');
      setPublicSite({ enabled: false, headline: '', welcomeText: '', heroImageUrl: '' });
    } catch (error) {
      setLogoUploading(false);
      const validationErrors = error.response?.data?.errors;
      const firstMsg = Array.isArray(validationErrors) && validationErrors.length > 0
        ? validationErrors[0].msg : null;
      toast.error(firstMsg || error.response?.data?.message || error.message || 'Failed to update organization');
    }
  };

  const handleDeleteOrganization = (orgId) => {
    setConfirmState({
      title: 'Delete Organization',
      message: 'Are you sure you want to delete this organization?',
      isDangerous: true,
      onConfirm: () => setOrganizations(prev => prev.filter(org => org.id !== orgId)),
    });
  };

  if (user?.role !== 'org_admin' && user?.role !== 'admin' && user?.role !== 'app_manager') {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <ShieldCheckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
          <p className="text-gray-500">Only organization administrators can access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading organizations...</p>
        </div>
      </div>
    );
  }

  // Resolve logo src — stored uploads start with /uploads/
  const logoSrc = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('/uploads/')) return logo;
    return logo; // external URL
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organization Management</h1>
            <p className="text-gray-600 mt-1">
              Manage organizations, teams, and administrative settings
            </p>
          </div>
          {['app_manager', 'org_admin'].includes(user?.role) && (
            <button
              onClick={() => { setEditingOrg(null); setLogoFile(null); setLogoPreview(null); setSelectedTheme(null); setSponsors([]); setSelectedParentOrgId(''); setShowAddForm(true); }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Organization
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`border-b-2 py-3 text-sm font-semibold ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'organizations' && (
      <>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Organizations</p>
              <p className="text-2xl font-bold text-gray-900">{organizations.length}</p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/team-management')}
          className="w-full text-left bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-green-500 hover:bg-green-50/50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
        >
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Teams</p>
              <p className="text-2xl font-bold text-gray-900">
                {organizations.reduce((total, org) => total + org.teams, 0)}
              </p>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2 font-medium">Click to manage teams →</p>
        </button>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Members</p>
              <p className="text-2xl font-bold text-gray-900">
                {organizations.reduce((total, org) => total + org.members, 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Coaches</p>
              <p className="text-2xl font-bold text-gray-900">
                {organizations.reduce((total, org) => total + org.coaches, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Organizations List */}
      <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          {organizations.length === 0 ? (
            <div className="text-center py-12">
              <BuildingOfficeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Organizations</h3>
              <p className="text-gray-500">You are not currently associated with any organizations.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Render root orgs first; child orgs are shown nested under their parent */}
              {(() => {
                const orgMap = new Map(organizations.map(o => [o.id, o]));
                // Root orgs: no parent, or parent not in the user's list
                const rootOrgs = organizations.filter(o => !o.parent_organization_id || !orgMap.has(o.parent_organization_id));
                // Standalone child orgs not under a root we have: show separately
                const renderedIds = new Set();
                const rows = [];
                rootOrgs.forEach(root => {
                  renderedIds.add(root.id);
                  // Collect children: from root.children list, cross-referenced with user's orgs
                  const childOrgs = (root.children || [])
                    .map(c => orgMap.get(c.id))
                    .filter(Boolean)
                    .filter(c => c.id !== root.id);
                  childOrgs.forEach(c => renderedIds.add(c.id));
                  rows.push({ root, childOrgs });
                });
                // Orgs that are children of orgs not in the user's list
                organizations.filter(o => !renderedIds.has(o.id)).forEach(o => {
                  rows.push({ root: o, childOrgs: [] });
                });
                return rows;
              })().map(({ root: org, childOrgs }) => (
                <div key={org.id} className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      {logoSrc(org.logo) && (
                        <img
                          src={logoSrc(org.logo)}
                          alt={`${org.name} logo`}
                          className="h-12 w-auto max-w-[160px] object-contain mb-2"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">{org.name}</h3>
                      {org.parent && (
                        <p className="text-xs text-blue-600 mb-0.5">
                          Part of: <span className="font-medium">{org.parent.name}</span>
                        </p>
                      )}
                      <p className="text-sm text-gray-600">{org.description || 'No description'}</p>
                      <p className="text-xs text-gray-500">
                        Code: {org.organizationCode} • Role: {org.role || 'member'}
                        {org.createdAt && ` • Created: ${new Date(org.createdAt).toLocaleDateString()}`}
                      </p>
                      {org.publicSite?.enabled && org.publicSlug && (
                        <a
                          href={`https://${org.publicSlug}.${process.env.REACT_APP_PUBLIC_SITE_ROOT_DOMAIN || 'fieldhouse.club'}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-flex text-xs font-semibold text-blue-600 hover:underline"
                        >
                          {org.publicSlug}.{process.env.REACT_APP_PUBLIC_SITE_ROOT_DOMAIN || 'fieldhouse.club'} ↗
                        </a>
                      )}
                      {org.colors?.primary && (
                        <div className="flex items-center gap-1 mt-1">
                          <span
                            className="inline-block h-3 w-3 rounded-full border border-gray-300"
                            style={{ background: org.colors.primary }}
                          />
                          <span
                            className="inline-block h-3 w-3 rounded-full border border-gray-300"
                            style={{ background: org.colors.secondary }}
                          />
                          {(() => {
                            const match = MLB_THEMES.find(t =>
                              t.primary.toLowerCase() === org.colors.primary.toLowerCase() &&
                              t.secondary.toLowerCase() === (org.colors.secondary || '').toLowerCase()
                            ) || MLB_THEMES.find(t => t.primary.toLowerCase() === org.colors.primary.toLowerCase());
                            return match ? <span className="text-xs text-gray-400 ml-1">{match.name}</span> : null;
                          })()}
                        </div>
                      )}
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      org.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {org.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{org.teams}</div>
                      <div className="text-xs text-gray-600">Teams</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{org.members}</div>
                      <div className="text-xs text-gray-600">Members</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{org.coaches}</div>
                      <div className="text-xs text-gray-600">Coaches</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => { setSelectedOrgForEmail(org); setShowEmailModal(true); }}
                        className="inline-flex items-center text-blue-600 hover:text-blue-900 text-sm font-medium"
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-1" />
                        Send Email
                      </button>
                      <button
                        onClick={() => { setSelectedOrgForSMS(org); setShowSMSModal(true); }}
                        className="inline-flex items-center text-green-600 hover:text-green-900 text-sm font-medium"
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                        Send SMS
                      </button>
                      {(org.role === 'owner' || org.role === 'admin' || user?.role === 'admin' || user?.role === 'app_manager') && (
                        <button
                          onClick={() => { setSelectedOrgForTemplates(org); setShowEmailTemplatesModal(true); }}
                          className="inline-flex items-center text-purple-600 hover:text-purple-900 text-sm font-medium"
                        >
                          <DocumentTextIcon className="h-4 w-4 mr-1" />
                          Email Templates
                        </button>
                      )}
                      <button className="text-blue-600 hover:text-blue-900 text-sm font-medium">
                        View Details
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditOrganization(org)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteOrganization(org.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                  {/* Child organizations nested under this parent */}
                  {childOrgs.length > 0 && (
                    <div className="ml-6 space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide px-1">
                        Member Organizations ({childOrgs.length})
                      </p>
                      {childOrgs.map(child => (
                        <div key={child.id} className="bg-white rounded-lg px-4 py-3 border border-gray-200 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {logoSrc(child.logo) && (
                              <img src={logoSrc(child.logo)} alt={child.name} className="h-7 w-auto max-w-[60px] object-contain" onError={(e) => { e.target.style.display='none'; }} />
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{child.name}</p>
                              <p className="text-xs text-gray-500">{child.description || child.organizationCode}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleEditOrganization(child)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Manage
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {activeTab === 'users' && (
        <div className="space-y-4">
          {organizations.length > 1 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center gap-3">
              <label className="text-sm font-semibold text-gray-700">Organization:</label>
              <select
                value={usersTabOrgId}
                onChange={e => setUsersTabOrgId(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              >
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}
          {usersTabOrgId && (
            <UserManagement organizationId={usersTabOrgId} embedded />
          )}
        </div>
      )}

      {activeTab === 'age-groups' && (
        <AgeGroupManagement organizations={organizations} />
      )}

      {/* Add/Edit Organization Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white mb-10">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingOrg ? 'Edit Organization' : 'Add New Organization'}
                </h3>
                <button
                  onClick={() => { setShowAddForm(false); setEditingOrg(null); setLogoFile(null); setLogoPreview(null); setSelectedTheme(null); setSponsors([]); setSponsorForm({ name: '', logo: '', website: '' }); setItemizationEnabled(false); setSchedulingEnabled(false); setLineItemTemplates([]); setLineItemForm({ name: '', description: '', default_amount: '', category: '' }); setEditingTemplate(null); setSelectedParentOrgId(''); setSelectedSport('baseball'); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={editingOrg ? handleUpdateOrganization : handleAddOrganization} className="space-y-5">
                {/* Organization Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingOrg?.name || ''}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Sport */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sport</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'baseball', label: '⚾ Baseball' },
                      { value: 'softball', label: '🥎 Softball' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSelectedSport(option.value)}
                        className={`rounded-lg border px-4 py-3 text-sm font-medium transition ${
                          selectedSport === option.value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Sets the default sport for teams and AI video analysis in this org.
                  </p>
                </div>

                {/* Parent Organization */}
                {organizations.filter(o => !o.parent_organization_id && o.id !== editingOrg?.id).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Organization</label>
                    <select
                      value={selectedParentOrgId}
                      onChange={(e) => setSelectedParentOrgId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">— None (standalone org) —</option>
                      {organizations
                        .filter(o => !o.parent_organization_id && o.id !== editingOrg?.id)
                        .map(o => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))
                      }
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Link this org under a parent, e.g. Team Elite Mid-West under Team Elite.
                    </p>
                  </div>
                )}

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Logo</label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="relative">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-16 w-auto max-w-[140px] object-contain rounded border border-gray-200 bg-gray-50 p-1"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <button
                          type="button"
                          onClick={() => { setLogoFile(null); setLogoPreview(null); if (logoInputRef.current) logoInputRef.current.value = ''; }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="h-16 w-16 rounded border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                        <PhotoIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoFileChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        <PhotoIcon className="h-4 w-4 mr-2" />
                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                      </label>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG, SVG — max 5 MB</p>
                    </div>
                  </div>
                </div>

                {/* MLB Theme Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    App Color Theme
                    <span className="ml-2 text-xs font-normal text-gray-500">— applies throughout the whole app</span>
                  </label>

                  {selectedTheme && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                      <span
                        className="inline-block h-5 w-5 rounded-full border border-gray-300 flex-shrink-0"
                        style={{ background: selectedTheme.primary }}
                      />
                      <span
                        className="inline-block h-5 w-5 rounded-full border border-gray-300 flex-shrink-0"
                        style={{ background: selectedTheme.secondary }}
                      />
                      <span className="text-sm font-medium text-gray-800">{selectedTheme.name}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedTheme(null)}
                        className="ml-auto text-xs text-gray-400 hover:text-gray-600"
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-5 gap-2 max-h-52 overflow-y-auto pr-1">
                    {MLB_THEMES.map((theme) => (
                      <button
                        key={theme.id}
                        type="button"
                        title={theme.name}
                        onClick={() => setSelectedTheme(theme)}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                          selectedTheme?.id === theme.id
                            ? 'border-gray-800 shadow-md scale-105'
                            : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <div className="flex gap-0.5">
                          <span
                            className="block h-5 w-5 rounded-full border border-gray-200"
                            style={{ background: theme.primary }}
                          />
                          <span
                            className="block h-5 w-5 rounded-full border border-gray-200"
                            style={{ background: theme.secondary }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-600 text-center leading-tight line-clamp-2">
                          {theme.name.replace(/^(New York|Los Angeles|San Francisco|San Diego|Kansas City|Tampa Bay|St\. Louis) /, (_, p1) => p1.split(' ').map(w => w[0]).join('') + ' ')}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sponsors & Partners */}
                {editingOrg && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sponsors &amp; Partners
                      <span className="ml-2 text-xs font-normal text-gray-500">— scrolling banner on the dashboard</span>
                    </label>

                    {/* Current sponsors list */}
                    {sponsors.length > 0 && (
                      <div className="mb-3 space-y-1.5">
                        {sponsors.map((sponsor) => (
                          <div key={sponsor.id} className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                            {sponsor.logo && (
                              <img
                                src={sponsor.logo}
                                alt={sponsor.name}
                                className="h-6 w-auto max-w-[60px] object-contain flex-shrink-0"
                                onError={e => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <span className="text-sm font-medium text-gray-800 flex-1 min-w-0 truncate">{sponsor.name}</span>
                            {sponsor.website && (
                              <a
                                href={sponsor.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:underline flex-shrink-0 hidden sm:block"
                              >
                                {sponsor.website.replace(/^https?:\/\//, '').split('/')[0]}
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => removeSponsor(sponsor.id)}
                              className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add sponsor form */}
                    <div className="border border-dashed border-gray-300 rounded-lg p-3 space-y-2">
                      <input
                        type="text"
                        placeholder="Sponsor name *"
                        value={sponsorForm.name}
                        onChange={e => setSponsorForm(f => ({ ...f, name: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSponsor(); } }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="url"
                        placeholder="Logo URL (optional)"
                        value={sponsorForm.logo}
                        onChange={e => setSponsorForm(f => ({ ...f, logo: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="url"
                        placeholder="Website URL (optional)"
                        value={sponsorForm.website}
                        onChange={e => setSponsorForm(f => ({ ...f, website: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={addSponsor}
                        disabled={!sponsorForm.name.trim()}
                        className="btn-primary flex items-center gap-1.5 px-3 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add Sponsor
                      </button>
                    </div>
                  </div>
                )}

                {/* Public organization website */}
                {editingOrg && (
                  ['owner', 'admin'].includes(editingOrg.role) || ['admin', 'app_manager'].includes(user?.role)
                ) && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900">Public organization website</label>
                        <p className="mt-0.5 text-xs text-gray-600">
                          Publish your branded homepage and open tryout registrations on a FieldHouse subdomain.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPublicSite(site => ({ ...site, enabled: !site.enabled }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
                          publicSite.enabled ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        role="switch"
                        aria-checked={publicSite.enabled}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                          publicSite.enabled ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>

                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Subdomain</label>
                        <div className="mt-1 flex rounded-lg shadow-sm">
                          <input
                            type="text"
                            value={publicSlug}
                            onChange={event => setPublicSlug(
                              event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                            )}
                            placeholder="elitesports"
                            className="min-w-0 flex-1 rounded-l-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="inline-flex items-center rounded-r-lg border border-l-0 border-gray-300 bg-gray-100 px-3 text-xs text-gray-600">
                            .{process.env.REACT_APP_PUBLIC_SITE_ROOT_DOMAIN || 'fieldhouse.club'}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Hero headline</label>
                        <input
                          type="text"
                          maxLength={160}
                          value={publicSite.headline}
                          onChange={event => setPublicSite(site => ({ ...site, headline: event.target.value }))}
                          placeholder={`Welcome to ${editingOrg.name}`}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Welcome copy</label>
                        <textarea
                          rows={4}
                          maxLength={2000}
                          value={publicSite.welcomeText}
                          onChange={event => setPublicSite(site => ({ ...site, welcomeText: event.target.value }))}
                          placeholder="Tell prospective families what makes your organization special."
                          className="mt-1 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Hero image URL</label>
                        <input
                          type="url"
                          value={publicSite.heroImageUrl}
                          onChange={event => setPublicSite(site => ({ ...site, heroImageUrl: event.target.value }))}
                          placeholder="https://..."
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Public email</label>
                          <input
                            type="email"
                            name="publicContactEmail"
                            defaultValue={editingOrg.contactInfo?.email || ''}
                            placeholder="info@example.com"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Public phone</label>
                          <input
                            type="tel"
                            name="publicContactPhone"
                            defaultValue={editingOrg.contactInfo?.phone || ''}
                            placeholder="(555) 555-5555"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      {publicSlug && publicSite.enabled && (
                        <a
                          href={`/?org=${encodeURIComponent(publicSlug)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex text-sm font-semibold text-blue-700 hover:underline"
                        >
                          Preview website ↗
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Scheduling — app manager only, controls whether this org can use Facilities & Scheduling */}
                {editingOrg && user?.role === 'app_manager' && (
                  <div className="mb-5 pb-5 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Scheduling
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Let this organization manage parks/facilities and auto-generate practice schedules.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSchedulingEnabled(v => !v)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${schedulingEnabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                        role="switch"
                        aria-checked={schedulingEnabled}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${schedulingEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Itemization — only shown when editing */}
                {editingOrg && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Itemization
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Define reusable line items (Dues, Tryouts, Facility Fees, etc.) that can be selected when creating invoices and fee requests.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleItemizationToggle(!itemizationEnabled)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${itemizationEnabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                        role="switch"
                        aria-checked={itemizationEnabled}
                      >
                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${itemizationEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>

                    {itemizationEnabled && (
                      <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
                        {/* Existing templates */}
                        {lineItemsLoading ? (
                          <p className="text-xs text-gray-400 text-center py-2">Loading items...</p>
                        ) : lineItemTemplates.length > 0 ? (
                          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                            {lineItemTemplates.map(t => (
                              <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200">
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium text-gray-800 truncate block">{t.name}</span>
                                  {(t.category || t.default_amount) && (
                                    <span className="text-xs text-gray-500">
                                      {t.category && <span className="mr-2">{t.category}</span>}
                                      {t.default_amount && <span>${parseFloat(t.default_amount).toFixed(2)}</span>}
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => startEditTemplate(t)}
                                  className="flex-shrink-0 text-gray-400 hover:text-blue-500 transition-colors"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteLineItemTemplate(t.id)}
                                  className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <TrashIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 text-center py-1">No items yet — add one below.</p>
                        )}

                        {/* Add / edit form */}
                        <div className="border border-dashed border-gray-300 rounded-lg p-3 bg-white space-y-2">
                          <p className="text-xs font-medium text-gray-600">
                            {editingTemplate ? 'Edit item' : 'Add item'}
                          </p>
                          <input
                            type="text"
                            placeholder="Item name *"
                            value={lineItemForm.name}
                            onChange={e => setLineItemForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <select
                            value={lineItemForm.category}
                            onChange={e => setLineItemForm(f => ({ ...f, category: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Category (optional)</option>
                            <option value="Dues">Dues</option>
                            <option value="Tryouts">Tryouts</option>
                            <option value="Facility Fees">Facility Fees</option>
                            <option value="Lessons">Lessons</option>
                            <option value="Equipment">Equipment</option>
                            <option value="Tournament">Tournament</option>
                            <option value="Camp / Clinic">Camp / Clinic</option>
                            <option value="Uniforms">Uniforms</option>
                            <option value="Other">Other</option>
                          </select>
                          <input
                            type="number"
                            placeholder="Default amount (optional)"
                            value={lineItemForm.default_amount}
                            onChange={e => setLineItemForm(f => ({ ...f, default_amount: e.target.value }))}
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <input
                            type="text"
                            placeholder="Description (optional)"
                            value={lineItemForm.description}
                            onChange={e => setLineItemForm(f => ({ ...f, description: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleSaveLineItemTemplate}
                              disabled={!lineItemForm.name.trim()}
                              className="btn-primary flex items-center gap-1.5 px-3 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <PlusIcon className="h-4 w-4" />
                              {editingTemplate ? 'Update' : 'Add Item'}
                            </button>
                            {editingTemplate && (
                              <button
                                type="button"
                                onClick={() => { setEditingTemplate(null); setLineItemForm({ name: '', description: '', default_amount: '', category: '' }); }}
                                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Type & Location — only shown for new orgs */}
                {!editingOrg && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                      <select
                        name="type"
                        defaultValue=""
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Type</option>
                        <option value="Youth Sports League">Youth Sports League</option>
                        <option value="Sports Club">Sports Club</option>
                        <option value="Recreation Department">Recreation Department</option>
                        <option value="School District">School District</option>
                        <option value="Private Organization">Private Organization</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                      <input
                        type="text"
                        name="location"
                        defaultValue=""
                        required
                        placeholder="City, State"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Established Year *</label>
                      <input
                        type="number"
                        name="established"
                        defaultValue=""
                        required
                        min="1900"
                        max="2030"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); setEditingOrg(null); setLogoFile(null); setLogoPreview(null); setSelectedTheme(null); setSponsors([]); setSponsorForm({ name: '', logo: '', website: '' }); setItemizationEnabled(false); setSchedulingEnabled(false); setLineItemTemplates([]); setLineItemForm({ name: '', description: '', default_amount: '', category: '' }); setEditingTemplate(null); setSelectedParentOrgId(''); setSelectedSport('baseball'); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={logoUploading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                  >
                    {logoUploading ? 'Uploading...' : editingOrg ? 'Update Organization' : 'Add Organization'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedOrgForEmail && (
        <OrganizationEmailModal
          organization={selectedOrgForEmail}
          onClose={() => { setShowEmailModal(false); setSelectedOrgForEmail(null); }}
          onSuccess={(data) => console.log('Email sent successfully:', data)}
        />
      )}

      {/* SMS Modal */}
      {showSMSModal && selectedOrgForSMS && (
        <OrganizationSMSModal
          organization={selectedOrgForSMS}
          onClose={() => { setShowSMSModal(false); setSelectedOrgForSMS(null); }}
          onSuccess={(data) => console.log('SMS sent successfully:', data)}
        />
      )}

      {/* Email Templates Modal */}
      {showEmailTemplatesModal && selectedOrgForTemplates && (
        <OrganizationEmailTemplatesModal
          organization={selectedOrgForTemplates}
          onClose={() => { setShowEmailTemplatesModal(false); setSelectedOrgForTemplates(null); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default OrganizationManagement;
